import { Link } from "react-router-dom";
import { Search, MapPin, ArrowRight, Star, BadgeCheck, ShoppingCart } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

// User-supplied category graphics (the five images from chat)
const CATEGORY_ART = {
  social: "https://customer-assets.emergentagent.com/job_ff5ff6a6-ab60-4aed-b35d-dded2ad0a697/artifacts/dhypaety_90C2F250-1E44-47A9-8998-8B693503F85A.png",
  travel: "https://customer-assets.emergentagent.com/job_ff5ff6a6-ab60-4aed-b35d-dded2ad0a697/artifacts/kd6eechs_1178BE12-9C54-4535-B15F-5D7568C783E9.png",
  medical: "https://customer-assets.emergentagent.com/job_ff5ff6a6-ab60-4aed-b35d-dded2ad0a697/artifacts/s0qnohbj_CEF98783-7873-4637-A501-CAD834B41E85.png",
  fitness: "https://customer-assets.emergentagent.com/job_ff5ff6a6-ab60-4aed-b35d-dded2ad0a697/artifacts/cyfhf36x_1CAABF56-CBE2-40DA-AD20-6DA299BA09D5.png",
  food: "https://customer-assets.emergentagent.com/job_ff5ff6a6-ab60-4aed-b35d-dded2ad0a697/artifacts/g1pcnoh6_5AA8DABE-99FD-4105-A49B-15E2E7585A10.png",
};

// ---------- OPTION A — Category Landing Page Hero (light editorial site, dark cinematic hero banner per category) ----------
function OptionA() {
  return (
    <div data-testid="preview-option-a" className="bg-white">
      {/* Hero banner uses the user's Travel graphic full-bleed */}
      <section className="relative w-full overflow-hidden">
        <div
          className="w-full h-[420px] sm:h-[520px] bg-center bg-cover"
          style={{ backgroundImage: `url(${CATEGORY_ART.travel})` }}
        />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex items-center justify-between">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-gray-500">Category</p>
            <h1 className="font-heading text-4xl sm:text-5xl text-tbc-navy mt-1">Travel &amp; Experiences</h1>
            <p className="text-gray-600 mt-2 max-w-xl">
              Black-owned travel advisors, tour operators, boutique stays, and curated experiences.
            </p>
          </div>
          <div className="hidden md:flex items-center gap-2 text-gray-500 text-sm">
            <span>184 businesses</span>
            <span className="w-1 h-1 rounded-full bg-gray-300" />
            <span>12 cities</span>
          </div>
        </div>
      </section>

      {/* Filters bar (existing site style) */}
      <section className="border-y border-gray-100 bg-tbc-cream/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex flex-wrap items-center gap-3">
          <div className="flex-1 min-w-[280px] flex items-center gap-2 bg-white rounded-full px-4 py-2.5 border border-gray-200">
            <Search className="w-4 h-4 text-gray-400" />
            <input className="flex-1 bg-transparent outline-none text-sm" placeholder="Search Travel businesses..." />
          </div>
          <div className="flex items-center gap-2 bg-white rounded-full px-4 py-2.5 border border-gray-200">
            <MapPin className="w-4 h-4 text-gray-400" />
            <input className="bg-transparent outline-none text-sm w-40" placeholder="City, State" />
          </div>
          <button className="rounded-full bg-tbc-navy text-white text-sm px-5 py-2.5">Search</button>
        </div>
      </section>

      {/* Business grid — existing site card style preserved */}
      <section className="py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { name: "Sankofa Voyages", city: "Brooklyn, NY", score: 9.4, verified: true,
                img: "https://images.unsplash.com/photo-1539635278303-d4002c07eae3?w=600&q=80" },
              { name: "Diaspora Stays", city: "Atlanta, GA", score: 8.8, verified: true,
                img: "https://images.unsplash.com/photo-1582719508461-905c673771fd?w=600&q=80" },
              { name: "Onyx Safaris", city: "Houston, TX", score: 9.1, verified: false,
                img: "https://images.unsplash.com/photo-1547471080-7cc2caa01a7e?w=600&q=80" },
            ].map((b) => (
              <div key={b.name} className="bg-white rounded-2xl border border-gray-100 overflow-hidden hover:shadow-md transition-shadow">
                <div className="aspect-[16/10] overflow-hidden">
                  <img src={b.img} alt={b.name} className="w-full h-full object-cover" />
                </div>
                <div className="p-5">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-heading text-lg text-tbc-navy">{b.name}</h3>
                    {b.verified && <BadgeCheck className="w-4 h-4 text-tbc-gold" />}
                  </div>
                  <p className="text-sm text-gray-500">{b.city}</p>
                  <div className="flex items-center gap-1 mt-2">
                    <Star className="w-4 h-4 fill-tbc-gold text-tbc-gold" />
                    <span className="text-sm font-medium text-tbc-navy">{b.score}</span>
                    <span className="text-xs text-gray-400">/ 10</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

// ---------- OPTION C — Dark luxury site-wide (homepage hero + category grid using these tiles) ----------
function OptionC() {
  const tiles = [
    { key: "social",  label: "Social Media & Influencers", img: CATEGORY_ART.social },
    { key: "travel",  label: "Travel & Experiences",       img: CATEGORY_ART.travel },
    { key: "medical", label: "Medical & Healthcare",       img: CATEGORY_ART.medical },
    { key: "fitness", label: "Health & Fitness",           img: CATEGORY_ART.fitness },
    { key: "food",    label: "Food & Beverage",            img: CATEGORY_ART.food },
    { key: "more",    label: "Browse all categories",      img: CATEGORY_ART.social, isMore: true },
  ];
  return (
    <div data-testid="preview-option-c" className="bg-[#0A0A0A] text-white">
      {/* Dark hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0A0A0A] via-[#0A0A0A] to-[#1a1208]" />
        <div className="absolute inset-0 opacity-30 mix-blend-screen"
          style={{ backgroundImage: "radial-gradient(ellipse at 70% 30%, rgba(212,175,55,0.35), transparent 55%)" }} />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <p className="text-[11px] uppercase tracking-[0.35em] text-tbc-gold">Build Black Wealth Together</p>
            <h1 className="mt-6 font-heading text-5xl sm:text-7xl leading-[1.05]">
              Discover & Support<br />
              <span className="text-tbc-gold italic">Black-Owned</span> Businesses
            </h1>
            <p className="mt-6 text-gray-400 max-w-xl">
              A curated marketplace of culture, craft, and commerce. Connecting you with authentic Black-owned businesses worth knowing.
            </p>
            <div className="mt-8 flex items-center gap-3 bg-black/40 backdrop-blur border border-white/10 rounded-full p-1.5 pr-2 max-w-xl">
              <div className="flex-1 flex items-center gap-2 px-4 py-2.5">
                <Search className="w-4 h-4 text-gray-500" />
                <input className="flex-1 bg-transparent outline-none text-sm text-white placeholder:text-gray-500" placeholder="Search businesses or products" />
              </div>
              <button className="rounded-full bg-tbc-gold text-black text-sm px-5 py-2.5 font-medium">Search</button>
            </div>
            <div className="mt-10 flex items-end gap-10">
              {[["500+", "Businesses"], ["10K+", "Products"], ["50K+", "Community"]].map(([n, l]) => (
                <div key={l}>
                  <p className="font-heading text-3xl text-tbc-gold">{n}</p>
                  <p className="text-xs uppercase tracking-widest text-gray-500 mt-1">{l}</p>
                </div>
              ))}
            </div>
          </div>
          {/* Hero art — use the Travel tile cropped */}
          <div className="lg:col-span-5 relative">
            <div className="aspect-square rounded-3xl overflow-hidden border border-tbc-gold/20 shadow-[0_30px_80px_-20px_rgba(212,175,55,0.25)]">
              <img src={CATEGORY_ART.travel} alt="" className="w-full h-full object-cover" />
            </div>
            <div className="absolute -bottom-6 -left-6 bg-black border border-tbc-gold/30 rounded-2xl px-4 py-3 hidden md:block">
              <p className="text-[10px] uppercase tracking-widest text-gray-500">Featured Category</p>
              <p className="text-tbc-gold font-heading text-lg">Travel & Experiences</p>
            </div>
          </div>
        </div>
      </section>

      {/* Category tiles using the user's graphics natively */}
      <section className="py-20 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between mb-10">
            <div>
              <p className="text-[11px] uppercase tracking-[0.35em] text-tbc-gold">Explore</p>
              <h2 className="font-heading text-4xl sm:text-5xl mt-3">Browse by Category</h2>
            </div>
            <Link to="#" className="hidden sm:inline-flex items-center gap-2 text-sm text-tbc-gold hover:gap-3 transition-all">
              All categories <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {tiles.map((t) => (
              <div
                key={t.key}
                className="group relative aspect-square rounded-2xl overflow-hidden border border-tbc-gold/20 cursor-pointer transition-transform hover:-translate-y-1"
              >
                <img src={t.img} alt={t.label} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                {/* Subtle hover overlay only — title is already in the artwork */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-5">
                  <span className="inline-flex items-center gap-2 text-sm text-tbc-gold">
                    Explore <ArrowRight className="w-4 h-4" />
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Sample product strip in dark theme so user sees how listings would feel */}
      <section className="py-20 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between mb-10">
            <div>
              <p className="text-[11px] uppercase tracking-[0.35em] text-tbc-gold">Featured</p>
              <h2 className="font-heading text-4xl sm:text-5xl mt-3">Top-rated this week</h2>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { name: "Heritage Hot Sauce", price: 14, by: "Onyx Pantry", img: "https://images.unsplash.com/photo-1604908176997-125f25cc6f3d?w=600&q=80" },
              { name: "Cowrie Earrings", price: 48, by: "Ife Studio", img: "https://images.unsplash.com/photo-1614701040020-3e85bba75c40?w=600&q=80" },
              { name: "Shea Body Butter", price: 22, by: "Akoma Skin", img: "https://images.unsplash.com/photo-1556228720-195a672e8a03?w=600&q=80" },
              { name: "Espresso Beans", price: 18, by: "Forty One Coffee", img: "https://images.unsplash.com/photo-1559056199-641a0ac8b55e?w=600&q=80" },
            ].map((p) => (
              <div key={p.name} className="bg-white/[0.03] border border-white/10 rounded-2xl overflow-hidden group">
                <div className="aspect-square overflow-hidden">
                  <img src={p.img} alt={p.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                </div>
                <div className="p-4">
                  <p className="text-xs text-gray-500">{p.by}</p>
                  <h3 className="font-heading text-lg mt-1">{p.name}</h3>
                  <div className="mt-3 flex items-center justify-between">
                    <span className="font-heading text-tbc-gold">${p.price}</span>
                    <button className="rounded-full bg-tbc-gold/15 hover:bg-tbc-gold/25 text-tbc-gold p-2 transition-colors">
                      <ShoppingCart className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

export default function PreviewDesignPage() {
  return (
    <div className="bg-gray-50">
      <Navbar />

      <div className="bg-tbc-navy text-white py-10 text-center">
        <p className="text-xs uppercase tracking-[0.3em] text-tbc-gold">Design Preview</p>
        <h1 className="font-heading text-3xl sm:text-4xl mt-2">Two directions for your category graphics</h1>
        <p className="text-gray-400 text-sm mt-2 max-w-2xl mx-auto px-4">
          This page is non-public. It exists only so you can compare Option A and Option C in context before we commit either to the live site.
        </p>
      </div>

      <div className="bg-gray-50 py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-tbc-navy text-white text-sm font-bold">A</span>
            <h2 className="font-heading text-2xl text-tbc-navy">Option A — Use the artwork as a category-page hero (light site stays as-is)</h2>
          </div>
          <p className="text-sm text-gray-600 mt-1 max-w-3xl">
            The current homepage and directory stay light/editorial. When a visitor clicks a category, that category&apos;s <em>own</em> landing page opens with your full-bleed graphic on top. Listings below use the existing card style.
          </p>
        </div>
      </div>

      <div className="border-y border-gray-200">
        <OptionA />
      </div>

      <div className="bg-gray-50 py-6 mt-2">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-black text-tbc-gold text-sm font-bold border border-tbc-gold/40">C</span>
            <h2 className="font-heading text-2xl text-tbc-navy">Option C — Commit to dark luxury site-wide</h2>
          </div>
          <p className="text-sm text-gray-600 mt-1 max-w-3xl">
            The entire site adopts a black + gold aesthetic that matches your artwork. The tiles below use your graphics natively — no extra title labels, because the artwork already brands itself.
          </p>
        </div>
      </div>

      <OptionC />

      <Footer />
    </div>
  );
}
